// One customer, as on the phone and more (D-163): who they are, the stage and her label, whose customer, details,
// people and numbers, every open follow-up with what can be done to it, notes and history, how they enquired, what they
// said, and every call with its recording.
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  directionLabel,
  fmtAgo,
  fmtDateTime,
  fmtDuration,
  fmtPhone,
  fromWallTime,
  isManagerRole,
  isUnanswered,
  TASK_TYPES,
  taskTypeLabel,
} from "../lib/format";
import {
  changedDetails,
  type CustomerPage,
  DETAIL_FIELDS,
  detailsOf,
  eventWords,
  followUpKind,
  followUpLabel,
  outcomeName,
  peopleOf,
  type StageRef,
  type TimelineEvent,
} from "../lib/crm";
import { visitWhen } from "../lib/visits";
import { factLabel } from "../lib/aiInstructions";
import type { LeadDetail as LeadData, LeadEnquiry, Source, TaskType } from "../lib/types";
import { useMember } from "../app/session";
import {
  Due,
  FollowUpActions,
  FollowUpModal,
  type FollowUpStart,
  KindBadge,
  LabelBadge,
  LabelPicker,
  StagePicker,
  type VisitMessage,
  WhatsAppModal,
} from "../app/crm";
import {
  Card,
  Empty,
  ErrorNote,
  Field,
  InterestBadge,
  Loading,
  Modal,
  Page,
  TaskSource,
  useAction,
  useLoad,
} from "../app/ui";

export function RescheduleModal(props: {
  title?: string;
  initialType: TaskType;
  initialLocal: string;
  tz: string;
  withReason?: boolean;
  onClose: () => void;
  onSave: (dueAtIso: string, type: TaskType, reason: string) => Promise<void>;
}) {
  const [type, setType] = useState<TaskType>(props.initialType);
  const [local, setLocal] = useState(props.initialLocal);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await props.onSave(
        fromWallTime(local, props.tz).toISOString(),
        type,
        reason,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={props.title ?? "Reschedule"} onClose={props.onClose}>
      <form onSubmit={submit}>
        <Field label="What">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as TaskType)}
          >
            {TASK_TYPES.map((t) => <option key={t} value={t}>{taskTypeLabel[t]}</option>)}
          </select>
        </Field>
        <Field label="When" hint={`Time in ${props.tz}`}>
          <input
            type="datetime-local"
            required
            value={local}
            onChange={(e) => setLocal(e.target.value)}
          />
        </Field>
        {props.withReason && (
          <Field label="Note">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Send the brochure first"
            />
          </Field>
        )}
        <div className="actions">
          <button type="button" onClick={props.onClose}>Cancel</button>
          <button className="primary" disabled={busy || !local}>Save</button>
        </div>
      </form>
    </Modal>
  );
}

// Erasing a customer on request (PRIV-01): a reason, whether to stop recording the number, and a deliberate tick.
function EraseModal(props: {
  name: string;
  onClose: () => void;
  onErase: (reason: string, neverRecord: boolean) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [neverRecord, setNeverRecord] = useState(true);
  const [sure, setSure] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await props.onErase(reason.trim(), neverRecord);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Erase ${props.name}`} onClose={props.onClose}>
      <form onSubmit={submit}>
        <p style={{ marginTop: 0 }}>
          Their calls, transcripts, AI notes and follow-ups are deleted now, and the recordings' audio within the hour.
          Their name and number are removed from past digests. This cannot be undone.
        </p>
        <Field label="Reason" hint="Kept in the audit log, without the customer's name or number.">
          <input
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Customer asked on a call"
          />
        </Field>
        <label className="row">
          <input type="checkbox" checked={neverRecord} onChange={(e) => setNeverRecord(e.target.checked)} />
          Never record this number again
        </label>
        <label className="row">
          <input type="checkbox" checked={sure} onChange={(e) => setSure(e.target.checked)} />
          I understand this cannot be undone
        </label>
        <div className="actions">
          <button type="button" onClick={props.onClose}>Cancel</button>
          <button className="danger solid" disabled={busy || !sure || !reason.trim()}>Erase</button>
        </div>
      </form>
    </Modal>
  );
}

export function LeadDetail() {
  const { id = "" } = useParams();
  const { api, member, tz } = useMember();
  // What the phone's customer page shows (D-163): stage, label, people and numbers, details, follow-ups, history.
  const page = useLoad(() => api.customerPage(id), [api, id]);
  // The calls with their recordings and what the AI read in them.
  const legacy = useLoad(() => api.lead(id), [api, id]);
  const members = useLoad(() => api.members(), [api]);
  // How they enquired: the ad, the form and its answers (LEAD-16).
  const enquiries = useLoad(() => api.leadEnquiries(id), [api, id]);
  const sources = useLoad(() => api.sources(), [api]);
  // The company's own names for the facts the AI notes (PIPE-14).
  const ai = useLoad(() => api.aiInstructions(), [api]);
  const { busy, run } = useAction();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<
    | { kind: "follow-up"; start: FollowUpStart }
    | { kind: "erase" }
    | { kind: "whatsapp"; visit?: VisitMessage }
    | null
  >(null);
  const manager = isManagerRole(member.role);
  const admin = member.role === "owner" || member.role === "admin";
  const names = new Map((members.data ?? []).map((m) => [m.id, m.display_name]));

  if (page.loading && !page.data) return <Loading />;
  if (!page.data) return <ErrorNote error={page.error ?? "Customer not found."} />;

  const { customer, follow_ups: followUps, timeline, stages } = page.data;
  const mayEdit = manager || customer.owner?.id === member.member_id;
  const reload = async () => {
    await Promise.all([page.reload(), legacy.reload()]);
  };
  const act = async (fn: () => Promise<unknown>, done: string) => {
    if (await run(fn, done)) await reload();
  };
  // The follow-up a new call takes the place of: the server keeps only visits and meetings beside it (TASK-01).
  const openCall = followUps.find((f) => f.type !== "visit" && f.type !== "meeting");
  const lead = legacy.data?.lead ?? null;
  const facts = lead?.facts && typeof lead.facts === "object" && !Array.isArray(lead.facts)
    ? Object.entries(lead.facts as Record<string, unknown>).filter(([k, v]) =>
      typeof v === "string" && v && !DETAIL_FIELDS.some((f) => f.key === k)
    )
    : [];
  const insights = legacy.data?.insights ?? [];
  const objections = insights.filter((i) => i.kind === "objection");
  const requests = insights.filter((i) => i.kind === "request");
  const title = customer.has_name ? customer.name : fmtPhone(customer.phone);

  return (
    <Page
      title={
        <span className="row">
          {title}
          <LabelBadge label={customer.label} />
        </span>
      }
      sub={
        <span className="row">
          <a href={`tel:${customer.phone}`}>{fmtPhone(customer.phone)}</a>
          <span className="faint">
            {customer.owner
              ? customer.owner.id === member.member_id ? "Your customer" : `${customer.owner.name}'s customer`
              : "Nobody's customer"}
            {customer.source ? ` · ${customer.source}` : ""}
            {customer.context ? ` · ${customer.context}` : ""}
          </span>
        </span>
      }
      actions={
        <>
          <a className="button" href={`tel:${customer.phone}`}>Call</a>
          <button onClick={() => setEditing({ kind: "whatsapp" })}>WhatsApp</button>
          <button
            className="primary"
            onClick={() => setEditing({ kind: "follow-up", start: { type: "call", assigned_to: customer.owner?.id } })}
          >
            Plan a follow-up
          </button>
        </>
      }
    >
      <div className="split">
        <div className="stack">
          <Card title="Follow-ups">
            {followUps.length === 0 && (
              <div className="row between">
                <span className="muted">
                  {customer.status === "open" ? "Nothing planned for this customer." : "Nothing planned."}
                </span>
              </div>
            )}
            <div className="list">
              {followUps.map((f) => (
                <div className="list-item follow-up" key={f.id}>
                  <div className="row between wrap">
                    <div className="row">
                      <KindBadge type={f.type} />
                      <Due dueAt={f.due_at} hasTime={f.due_has_time} tz={tz} />
                      <span className="faint">
                        {f.assigned_to === member.member_id ? "You" : names.get(f.assigned_to ?? "") ?? ""}
                        {f.place ? ` · ${f.place}` : ""}
                        {f.booked_by && f.booked_by !== f.assigned_to
                          ? ` · booked by ${
                            f.booked_by === member.member_id ? "you" : names.get(f.booked_by) ?? "a colleague"
                          }`
                          : ""}
                      </span>
                      {followUpKind(f.type) === "visit" && (
                        <button
                          className="small"
                          onClick={() =>
                            setEditing({
                              kind: "whatsapp",
                              visit: {
                                // The message signs off as whoever booked the visit (D-186).
                                caller: f.booked_by === member.member_id || !f.booked_by
                                  ? member.display_name
                                  : names.get(f.booked_by) ?? member.display_name,
                                details: {
                                  ...visitWhen(f.due_at, tz),
                                  place: f.place ?? member.visits?.place,
                                  map: member.visits?.map_link,
                                  handler: f.assigned_to === member.member_id
                                    ? member.display_name
                                    : names.get(f.assigned_to ?? "") ?? null,
                                  company: member.org_name,
                                },
                              },
                            })}
                        >
                          Send visit details
                        </button>
                      )}
                    </div>
                    <FollowUpActions
                      followUp={f}
                      onChange={() => void reload()}
                      onEdit={() =>
                        setEditing({
                          kind: "follow-up",
                          start: {
                            id: f.id,
                            type: f.type,
                            due_at: f.due_at,
                            due_has_time: f.due_has_time,
                            assigned_to: f.assigned_to,
                            place: f.place,
                          },
                        })}
                    />
                  </div>
                  <div className="follow-up-why">{f.brief || f.reason}</div>
                  <div className="faint">
                    <TaskSource source={f.source} rule={f.rule} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <History
            leadId={customer.id}
            timeline={timeline}
            stages={stages}
            names={names}
            tz={tz}
            onNoted={() => void page.reload()}
          />

          <Calls data={legacy.data} error={legacy.error} names={names} tz={tz} />
        </div>

        <div className="stack">
          <Card title="Customer">
            <Field label="Stage">
              <StagePicker
                stages={stages}
                stageId={customer.stage_id}
                busy={busy}
                onChange={(stageId, reason) =>
                  act(
                    () => api.setStage(customer.id, stageId, reason),
                    `Moved to ${stages.find((s) => s.id === stageId)?.name ?? "the stage"}`,
                  )}
              />
            </Field>
            {customer.status !== "open" && customer.lost_reason && (
              <div className="faint" style={{ marginTop: -6, marginBottom: 10 }}>Why: {customer.lost_reason}</div>
            )}
            <Field label="Label">
              <LabelPicker
                label={customer.label}
                busy={busy}
                onChange={(label) =>
                  act(() => api.setLabel(customer.id, label), label ? `Marked ${label}` : "Label removed")}
              />
            </Field>
            <Field
              label="Whose customer"
              hint={manager ? "Their open follow-ups go along." : "Your manager can hand customers over."}
            >
              <select
                value={customer.owner?.id ?? ""}
                disabled={!manager || busy}
                onChange={(e) =>
                  act(
                    () => api.assignCustomers([customer.id], e.target.value || null),
                    e.target.value
                      ? `Handed to ${names.get(e.target.value) ?? "them"}`
                      : "Left with nobody; its open follow-ups are yours now",
                  )}
              >
                <option value="">Nobody</option>
                {(members.data ?? []).filter((m) => m.status === "active" || m.id === customer.owner?.id).map((
                  m,
                ) => <option key={m.id} value={m.id}>{m.display_name}</option>)}
              </select>
            </Field>
            <div className="customer-numbers faint">
              {customer.calls > 0
                ? `${customer.calls} ${
                  customer.calls === 1 ? "call" : "calls"
                }, ${customer.connected_calls} connected` +
                  (customer.last_contact_at ? ` · last ${fmtAgo(customer.last_contact_at, tz)}` : "")
                : "Never called"}
              {customer.enquired_at ? ` · enquired ${fmtAgo(customer.enquired_at, tz)}` : ""}
            </div>
            {(lead?.summary || facts.length > 0 || (lead && lead.interest !== "none")) && (
              <div className="ai-reading">
                <div className="row">
                  <strong>AI's reading</strong>
                  {lead && <InterestBadge interest={lead.interest} />}
                </div>
                {lead?.summary && <p>{lead.summary}</p>}
                {facts.length > 0 && (
                  <div className="chips">
                    {facts.map(([k, v]) => (
                      <span className="badge" key={k}>
                        {factLabel(k, ai.data?.facts ?? [])}: {String(v)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>

          <Details
            leadId={customer.id}
            name={customer.has_name ? customer.name : ""}
            details={detailsOf(customer.details)}
            mayEdit={mayEdit}
            onSaved={() => void reload()}
          />

          <People page={page.data} mayEdit={mayEdit} onChanged={() => void page.reload()} />

          {(enquiries.data ?? []).length > 0 && (
            <Card title="How they enquired">
              <Enquiries enquiries={enquiries.data!} sources={sources.data ?? []} tz={tz} />
            </Card>
          )}

          {(objections.length > 0 || requests.length > 0) && (
            <Card title="What they said">
              <div className="list">
                {objections.map((o) => (
                  <div className="list-item" key={o.id}>
                    <div className="row between">
                      <strong>{o.detail || o.category}</strong>
                      {o.t != null && (
                        <Link className="moment" to={`/calls/${o.recording_id}?t=${o.t}`}>
                          ▶ {fmtDuration(o.t)}
                        </Link>
                      )}
                    </div>
                    {o.quote && <div className="quote">“{o.quote}”</div>}
                  </div>
                ))}
                {requests.map((r) => <div className="list-item" key={r.id}>Asked: {r.detail}</div>)}
              </div>
            </Card>
          )}

          {admin && (
            <Card title="Customer's data">
              <p className="muted" style={{ marginTop: 0 }}>
                If this customer asks for their data to be deleted, erase them here.
              </p>
              <button className="small danger" onClick={() => setEditing({ kind: "erase" })}>Erase customer…</button>
            </Card>
          )}
        </div>
      </div>

      {editing?.kind === "follow-up" && (
        <FollowUpModal
          title={editing.start.id ? "Change the follow-up" : "Plan a follow-up"}
          leadId={customer.id}
          start={editing.start}
          replaces={openCall
            ? `This takes the place of the open ${
              followUpLabel(openCall.type).toLowerCase()
            } follow-up (one at a time; ` +
              "visits and meetings sit beside it)."
            : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
      {editing?.kind === "whatsapp" && (
        <WhatsAppModal
          leadId={customer.id}
          phone={customer.phone}
          customerName={customer.has_name ? customer.name : null}
          company={member.org_name}
          visit={editing.visit}
          onClose={() => setEditing(null)}
          onSent={() => {
            setEditing(null);
            void page.reload();
          }}
        />
      )}
      {editing?.kind === "erase" && (
        <EraseModal
          name={title}
          onClose={() => setEditing(null)}
          onErase={async (reason, neverRecord) => {
            if (await run(() => api.eraseLead(customer.id, reason, neverRecord), "Customer erased")) {
              navigate("/leads");
            }
          }}
        />
      )}
    </Page>
  );
}

/** Notes and everything that happened to the customer, newest first; calls have their own card. */
function History(props: {
  leadId: string;
  timeline: TimelineEvent[];
  stages: StageRef[];
  names: Map<string, string>;
  tz: string;
  onNoted: () => void;
}) {
  const { api } = useMember();
  const [note, setNote] = useState("");
  const [all, setAll] = useState(false);
  const { busy, run } = useAction();
  const stageNames = new Map(props.stages.map((s) => [s.id, s.name]));
  const events = props.timeline
    .filter((e) => e.type !== "call.logged")
    .map((e) => ({ e, words: eventWords(e, stageNames, props.names) }))
    .filter((x) => x.words);
  const shown = all ? events : events.slice(0, 12);
  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (await run(() => api.addNote(props.leadId, note), "Note added")) {
      setNote("");
      props.onNoted();
    }
  };
  return (
    <Card title="Notes and history">
      <form className="note-form" onSubmit={submit}>
        <textarea
          aria-label="A note on this customer"
          placeholder="Add a note: what they want, what was agreed…"
          value={note}
          maxLength={2000}
          rows={2}
          onChange={(e) => setNote(e.target.value)}
        />
        <button className="primary small" disabled={busy || !note.trim()}>Add note</button>
      </form>
      {events.length === 0 && <div className="faint">Nothing yet.</div>}
      <ol className="history">
        {shown.map(({ e, words }) => (
          <li key={e.id} className={e.type === "note.logged" ? "note" : undefined}>
            <span className="history-when faint nowrap">{fmtDateTime(e.at, props.tz)}</span>
            <span className="history-what">
              {e.type === "note.logged" ? <span className="note-text">{String(e.payload.text ?? "")}</span> : words}
              {e.by && <span className="faint">{" · "}{e.by}</span>}
              {!e.by && e.actor !== "member" && <span className="faint">{" · "}Voxowa</span>}
            </span>
          </li>
        ))}
      </ol>
      {events.length > shown.length && (
        <button className="link" onClick={() => setAll(true)}>Show all {events.length}</button>
      )}
    </Card>
  );
}

/** The customer's name and what they are looking for, where, for how much and by when (as on the phone, C5). */
function Details(props: {
  leadId: string;
  name: string;
  details: Record<string, string>;
  mayEdit: boolean;
  onSaved: () => void;
}) {
  const { api } = useMember();
  const [name, setName] = useState(props.name);
  const [values, setValues] = useState(props.details);
  const { busy, run } = useAction();
  const changed = changedDetails(props.details, values);
  const dirty = Object.keys(changed).length > 0 || name.trim() !== props.name.trim();
  if (!props.mayEdit) {
    const filled = DETAIL_FIELDS.filter((f) => props.details[f.key]);
    return (
      <Card title="Details">
        {filled.length === 0 && <div className="faint">No details yet.</div>}
        <dl className="answers">
          {filled.map((f) => (
            <div key={f.key}>
              <dt>{f.label}</dt>
              <dd>{props.details[f.key]}</dd>
            </div>
          ))}
        </dl>
        <div className="faint">Only the customer's owner or a manager can change these.</div>
      </Card>
    );
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const newName = name.trim() !== props.name.trim() ? name.trim() : null;
    if (await run(() => api.saveCustomerDetails(props.leadId, newName, changed), "Saved")) props.onSaved();
  };
  return (
    <Card title="Details">
      <form onSubmit={submit}>
        <Field label="Name">
          <input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} placeholder="Their name" />
        </Field>
        {DETAIL_FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            <input
              value={values[f.key] ?? ""}
              maxLength={200}
              placeholder={f.placeholder}
              onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
            />
          </Field>
        ))}
        {dirty && (
          <div className="actions">
            <button
              type="button"
              onClick={() => {
                setName(props.name);
                setValues(props.details);
              }}
            >
              Undo
            </button>
            <button className="primary" disabled={busy}>Save</button>
          </div>
        )}
      </form>
    </Card>
  );
}

/** The people at the customer and their numbers; another number can be added, and any but the main one removed. */
function People(props: { page: CustomerPage; mayEdit: boolean; onChanged: () => void }) {
  const { api } = useMember();
  const [adding, setAdding] = useState(false);
  const [phone, setPhone] = useState("");
  const [person, setPerson] = useState("");
  const [role, setRole] = useState("");
  const { busy, run } = useAction();
  const { people, loose } = peopleOf(props.page);
  const number = (n: CustomerPage["numbers"][number]) => (
    <div className="row between number-line" key={n.id}>
      <span>
        <a href={`tel:${n.e164}`}>{fmtPhone(n.e164)}</a>
        {n.label && <span className="faint">{" · "}{n.label}</span>}
        {n.is_primary && <span className="faint">{" · "}main</span>}
        {n.is_whatsapp && <span className="faint">{" · "}WhatsApp</span>}
      </span>
      {props.mayEdit && !n.is_primary && (
        <button
          className="small ghost"
          disabled={busy}
          onClick={async () => {
            if (await run(() => api.removeCustomerNumber(n.id), "Number removed")) props.onChanged();
          }}
        >
          Remove
        </button>
      )}
    </div>
  );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await run(() => api.addCustomerNumber(props.page.customer.id, phone, person, role), "Number added")) {
      setPhone("");
      setPerson("");
      setRole("");
      setAdding(false);
      props.onChanged();
    }
  };
  return (
    <Card
      title="People and numbers"
      actions={props.mayEdit && !adding && (
        <button className="small" onClick={() => setAdding(true)}>Add a number</button>
      )}
    >
      <div className="list">
        {people.map(({ contact, numbers }) => (
          <div className="list-item" key={contact.id}>
            <div>
              <strong>{contact.name || "No name"}</strong>
              {contact.role && <span className="faint">{" · "}{contact.role}</span>}
              {contact.is_primary && <span className="faint">{" · "}main contact</span>}
            </div>
            {numbers.map(number)}
          </div>
        ))}
        {loose.length > 0 && <div className="list-item">{loose.map(number)}</div>}
      </div>
      {adding && (
        <form onSubmit={submit} className="add-number">
          <Field label="Number">
            <input
              required
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="98765 43210"
            />
          </Field>
          <Field label="Their name (optional)">
            <input value={person} maxLength={80} onChange={(e) => setPerson(e.target.value)} />
          </Field>
          <Field label="Who they are (optional)">
            <input
              value={role}
              maxLength={60}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Wife, brother, office"
            />
          </Field>
          <div className="actions">
            <button type="button" onClick={() => setAdding(false)}>Cancel</button>
            <button className="primary" disabled={busy || !phone.trim()}>Add</button>
          </div>
        </form>
      )}
    </Card>
  );
}

/** Every call with its recording and what the AI read in it, and recordings no call was found for. */
function Calls(props: { data: LeadData | null; error: string | null; names: Map<string, string>; tz: string }) {
  if (!props.data) {
    return props.error ? <ErrorNote error={props.error} /> : null;
  }
  const { calls, recordings, analyses } = props.data;
  const recByCall = new Map(recordings.filter((r) => r.call_id).map((r) => [r.call_id!, r]));
  const analysisByRec = new Map(analyses.map((a) => [a.recording_id, a]));
  const unlinked = recordings.filter((r) => !r.call_id);
  return (
    <Card title="Calls" flush>
      {calls.length === 0 && unlinked.length === 0 && <Empty>No calls yet.</Empty>}
      <div className="table-wrap">
        <table>
          <tbody>
            {calls.map((c) => {
              const rec = recByCall.get(c.id);
              const analysis = rec ? analysisByRec.get(rec.id) : undefined;
              return (
                <tr key={c.id}>
                  <td className="nowrap">
                    <div className="primary">{fmtDateTime(c.started_at, props.tz)}</div>
                    <div className="faint">
                      {directionLabel[c.direction]} · {fmtDuration(c.duration_s)} · {props.names.get(c.member_id) ?? ""}
                      {isUnanswered(c.direction, c.duration_s, c.call_status) && (
                        <span className="badge unanswered">Unanswered</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {c.outcome && <span className="badge">{c.disposition ?? outcomeName(c.outcome)}</span>}
                    {analysis?.brief && <div>{analysis.brief}</div>}
                    {rec && rec.status !== "done" && (
                      <span className="faint">Recording {rec.status === "held" ? "waiting" : rec.status}</span>
                    )}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {rec && (
                      <Link className="button small" to={`/calls/${rec.id}`}>
                        {analysis ? "Open call" : "Recording"}
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
            {unlinked.map((r) => (
              <tr key={r.id}>
                <td className="nowrap">
                  <div className="primary">{r.recorded_at ? fmtDateTime(r.recorded_at, props.tz) : "Recording"}</div>
                  <div className="faint">{fmtDuration(r.duration_s)} · not matched to a call</div>
                </td>
                <td>{analysisByRec.get(r.id)?.brief}</td>
                <td style={{ textAlign: "right" }}>
                  <Link className="button small" to={`/calls/${r.id}`}>Open call</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const PLATFORMS: Record<string, string> = {
  fb: "Facebook",
  ig: "Instagram",
  an: "Audience Network",
  msg: "Messenger",
  indiamart: "IndiaMART",
  justdial: "JustDial",
};

/** " · Instagram" for an Instagram lead from the Facebook ad source; nothing when the source already says it. */
function platform(code: string | null, source: string | undefined): string {
  if (!code) return "";
  const name = PLATFORMS[code] ?? code;
  return source?.toLowerCase().includes(name.toLowerCase()) ? "" : ` · ${name}`;
}

/** "which_course_are_you_interested_in?" reads "Which course are you interested in?". */
function question(q: string): string {
  const text = q.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Each time the customer enquired, newest first: where from, which ad, and what they filled in. */
function Enquiries(props: { enquiries: LeadEnquiry[]; sources: Source[]; tz: string }) {
  const named = (name: string | null, id: string | null) => name && id ? `${name} (${id})` : name ?? id;
  return (
    <div className="list">
      {props.enquiries.map((e) => {
        const answers = Array.isArray(e.answers) ? (e.answers as { q: string; a: string }[]) : [];
        const where = [
          ["Campaign", named(e.campaign_name, e.campaign_id)],
          ["Ad set", named(e.adset_name, e.adset_id)],
          ["Ad", named(e.ad_name, e.ad_id)],
          ["Form", named(e.form_name, e.form_id)],
        ].filter(([, v]) => v);
        return (
          <div className="list-item" key={e.id}>
            <div className="row between">
              <strong>
                {props.sources.find((s) => s.id === e.source_id)?.name ?? "Import"}
                {platform(e.platform, props.sources.find((s) => s.id === e.source_id)?.name)}
                {e.is_organic ? " · organic" : ""}
              </strong>
              <span className="faint">
                {e.time_from_file
                  ? fmtDateTime(e.enquired_at, props.tz)
                  : `Imported ${fmtDateTime(e.enquired_at, props.tz)}`}
              </span>
            </div>
            {where.map(([label, value]) => (
              <div className="faint" key={label}>
                {label}: {value}
              </div>
            ))}
            {answers.length > 0 && (
              <dl className="answers">
                {answers.map((a, i) => (
                  <div key={i}>
                    <dt>{question(a.q)}</dt>
                    <dd>{a.a}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        );
      })}
    </div>
  );
}
